import cls from "classnames";
import { useState, useEffect, Fragment } from "react";
import * as Sentry from "@sentry/nextjs";

import useSessionStore from "../../../store/sessionStore";
import * as Track from "@qoohoo/common/utils/analytics/track";
import { api } from "@qoohoo/common/constants/analytics";
import Spinner from "@qoohoo/common/library/loader/Spinner";
import toast from "react-hot-toast";
import { Transition } from "@headlessui/react";
import { getPageNameByUrl } from "@qoohoo/common/utils/analytics";
import httpf from "../../../utils/frontend/httpf";

const modal_name = "payment_consent_modal";

const getSanitizedConsentTerms = (str) => {
  if(!str) return [];

  let terms = [];

  // remove whitespace, lines at ends
  terms = str.trim().split("\n");

  // remove empty values (e.g only space or only newlines)
  terms = terms.filter(term=>!!(term.trim()));
  
  terms = terms.map(term=>{
    const termAfterBold = term.replace(/\*([^\*]+)\*/g,"<b>$1</b>");
    const termAfterBoldAndItalic = termAfterBold.replace(/_([^_]+)_/, "<i>$1</i>");

    return termAfterBoldAndItalic;
  });

  return terms;
}

const PaymentConsentTermsModal = ({ onClose, onAccept, paymentConsentTerms, channelDetails, isPremiumContent=false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const userData = useSessionStore((state) => state.userData);
  const channelId = channelDetails.uid;

  const consentTerms = getSanitizedConsentTerms(paymentConsentTerms);
  const isConsentTermsTooLong = paymentConsentTerms?.length > 1200;

  const isOneToOne = channelDetails?.type?.toLowerCase()==="one-to-one";
  const isChannel = ["one_time" ,"multi_day_session", "recurring", "free_subscription"].indexOf(channelDetails?.type?.toLowerCase()) > -1;
  const isCourse = channelDetails?.type?.toLowerCase()==="course";

  let current_screen = "";
  if(typeof window !== "undefined"){
    current_screen = getPageNameByUrl(window.location.href);
  }

  const trackDetails = {
    current_user: userData?.username,
    current_screen: current_screen,
    modal_name,
    channel_id: channelId,
    user_id: userData.uid,
  };

  const onAcceptApi = async () => {
    setIsLoading(true);
    try {
      const payload = {
        accepted: true,
      }

      if(isChannel || isCourse) {
        payload.channel = channelId;
      } else if (isPremiumContent) {
        payload.premium_content=channelId;
      } else if(isOneToOne) {
        payload.one_to_one_meeting=channelId;
      }

      await httpf.post(`/api/log-payment-consent`, payload);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);

      Track.customEvent(api.payment_consent_api, {
        type: "failed",
        ...trackDetails
      });

      throw error;
    }
  };

  const onReject = () => {
    Track.customEvent(modal_name, {
      actionType: "modal_close",
      label: modal_name,
      ...trackDetails,
    });

    onClose();
  };

  const onAcceptClick = async () => {    
    if(isLoading) {
      return;
    }

    try {
      await onAcceptApi();
      
      Track.customEvent(api.payment_consent_api , {
        type: "success",
        ...trackDetails
      });

      onAccept();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      
      console.error(error);
      Sentry.captureException(error);
    }
  };

  useEffect(()=>{
  Track.customEvent(modal_name, {
      actionType: "modal_open",
      label: modal_name,
      ...trackDetails,
    });
  }, []);

  return (
    <Transition
      as={Fragment}
      show={true}
      appear={true}
      enter="ease-out duration-150"
      enterFrom="scale-95"
      enterTo="scale-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="sm:inline-block sm:align-middle h-screen sm:h-full">
        <div className="relative inline-flex items-end justify-center w-full h-full sm:items-center font-nunito sm:py-14">
          <div className={cls(
            'relative bg-white sm:rounded-sm max-w-full w-full h-full sm:h-[526px]',
            isConsentTermsTooLong ? 'sm:max-w-[640px] sm:w-[640px]' : 'sm:max-w-[344px] sm:w-[344px]'
          )}>
            {onClose && (
              <img
                src="/assets/icons/cancel.svg"
                lt="close button"
                onClick={onReject}
                className="absolute z-10 cursor-pointer select-none right-5 top-6"
              />
            )}
            <div className="flex flex-col h-full w-full relative">
              <div className="relative px-5 pt-6 flex flex-col flex-auto w-full min-h-0 overflow-hidden">
                <div className="font-semibold text-xl text-[#22272B] leading-6 text-left font-nunito flex-shrink-0">
                  Terms
                </div>
                <div className="font-nunito text-sm leading-6 text-[#000000] flex-auto min-h-0 mt-2 overflow-y-scroll hide-scrollbar pb-20">
                  <ul className="list-none text-left list-inside pb-[48px] sm:pb-0">
                    {consentTerms.map((term, index) => (
                      <li key={index} className="mb-2">
                        <span
                          className="before:content-['•'] before:text-[13px] before:mr-3 leading-5 align-text-top"
                          dangerouslySetInnerHTML={{ __html: term }}
                        ></span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="absolute left-0 bottom-0 flex-shrink-0 w-full flex items-center justify-center sm:justify-end px-5 pt-3 pb-8 sm:pb-4 gradient-fade-white border-t border-[#22272B] border-opacity-20">
                <div
                  className={cls("bg-[#605FE7] hover:bg-opacity-90 px-4 py-[10px] w-full sm:w-[92px]" +
                    " h-[48px] sm:h-[40px] rounded-full overflow-hidden cursor-pointer flex items-center justify-center font-semibold text-base sm:text-[14px] leading-5 text-center",
                    {"opacity-40 cursor-not-allowed" : isLoading},
                  )}
                  onClick={onAcceptClick}
                >
                  {isLoading ? <Spinner className="!h-4 !w-4" /> : "I Accept"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default PaymentConsentTermsModal;
